import React from 'react';
import { func, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { pathForLocale } from '../../util/locale';
import { PrimaryButtonInline } from '../Button/Button';
import Icon from '../Icon/Icon';

import css from './ErrorPage.module.css';

export const SUPPORT_EMAIL = 'team@attractr.live';

const reload = () => {
  if (typeof window !== 'undefined') {
    window.location.reload();
  }
};

/**
 * The 500 page from the Components board ("Something went wrong on our side"), shown by the app's
 * error boundary in place of a page that crashed. "Go to home" is a full page load and "Try
 * again" reloads, so a broken app state (or a code chunk a deploy removed) doesn't come back.
 * `errorReference` is the Sentry event id; the line only shows when there is one.
 * `public/500.html` is the same page for errors the server can't render.
 */
const ErrorPage = props => {
  const { rootClassName, className, errorReference, onTryAgain } = props;
  const intl = useIntl();
  const classes = classNames(rootClassName || css.root, className);
  const email = (
    <a className={css.email} href={`mailto:${SUPPORT_EMAIL}`}>
      {SUPPORT_EMAIL}
    </a>
  );

  return (
    <main className={classes}>
      <div className={css.content}>
        <span className={css.icon} aria-hidden="true">
          <Icon name="warning" size={24} />
        </span>
        <h1 className={css.title}>
          <FormattedMessage id="ErrorPage.title" />
        </h1>
        <p className={css.text}>
          <FormattedMessage id="ErrorPage.text" values={{ email }} />
        </p>
        <div className={css.actions}>
          <a className={css.homeLink} href={pathForLocale('/', intl.locale)}>
            <FormattedMessage id="ErrorPage.goToHome" />
          </a>
          <PrimaryButtonInline type="button" onClick={onTryAgain || reload}>
            <FormattedMessage id="ErrorPage.tryAgain" />
          </PrimaryButtonInline>
        </div>
        {errorReference ? (
          <p className={css.reference}>
            <FormattedMessage id="ErrorPage.reference" values={{ reference: errorReference }} />
          </p>
        ) : null}
      </div>
    </main>
  );
};

ErrorPage.defaultProps = {
  rootClassName: null,
  className: null,
  errorReference: null,
  onTryAgain: null,
};

ErrorPage.propTypes = {
  rootClassName: string,
  className: string,
  // The Sentry event id of the error, for support
  errorReference: string,
  // Defaults to reloading the page
  onTryAgain: func,
};

export default ErrorPage;
